import { useEffect, useState } from "react";

/** A viewport layer that stays in the active dialog's DOM for its keyboard focus trap. */
export function useFeedbackHost(enabled = true) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!enabled) {
      setHost(null);
      return;
    }
    const layer = document.createElement("div");
    layer.setAttribute("data-feedback-layer", "");
    const topLayer = typeof layer.showPopover === "function";
    if (topLayer) layer.setAttribute("popover", "manual");
    // Native popovers escape transformed/scrolling dialog ancestors. Their DOM
    // ancestry still lets MUI's focus trap accept the toast's recovery actions.
    Object.assign(layer.style, {
      position: "fixed",
      inset: "0",
      margin: "0",
      padding: "0",
      border: "0",
      width: "100%",
      height: "100%",
      maxWidth: "none",
      maxHeight: "none",
      boxSizing: "border-box",
      overflow: "visible",
      background: "transparent",
      pointerEvents: "none",
    });
    const update = () => {
      const modals = Array.from(
        document.querySelectorAll<HTMLElement>(
          '.MuiModal-root:not([aria-hidden="true"])',
        ),
      );
      const active = modals[modals.length - 1];
      // Older engines without popovers use the viewport-sized modal root.
      const target =
        (topLayer
          ? active?.querySelector<HTMLElement>('[role="dialog"]')
          : null) ??
        active ??
        document.body;
      if (layer.parentElement !== target) {
        if (topLayer && layer.isConnected) layer.hidePopover();
        target.appendChild(layer);
        if (topLayer) layer.showPopover();
      }
      // ModalManager may have hidden this node when it was a body sibling.
      if (layer.hasAttribute("aria-hidden"))
        layer.removeAttribute("aria-hidden");
    };
    update();
    setHost(layer);
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-hidden"],
    });
    return () => {
      observer.disconnect();
      layer.remove();
    };
  }, [enabled]);
  return host;
}
